import React from 'react';
import { Modal, Form, InputNumber, Input, Button, Space, message } from 'antd';
import { useCustom } from '@refinedev/core';
import { apiClient } from '@src/utils/axios';

interface StockAdjustmentFormProps {
  visible: boolean;
  onCancel: () => void;
  record: any;
  adjustmentType: 'increase' | 'decrease';
  onSuccess?: () => void;
}

interface FormValues {
  quantity: number;
  reason: string;
}

export const StockAdjustmentForm: React.FC<StockAdjustmentFormProps> = ({
  visible,
  onCancel,
  record,
  adjustmentType,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = React.useState(false);

  // Set initial values when modal opens
  React.useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        currentStock: record.current_stock || 0,
        quantity: 1,
        reason: '',
      });
    }
  }, [visible, record, form]);

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsLoading(true);
      const adjustmentData = {
        type: adjustmentType,
        quantity: values.quantity,
        reason: values.reason,
      };

      await apiClient.put(`/inventory/adjust/${record.id}`, adjustmentData);

      message.success(
        `Stock ${adjustmentType === 'increase' ? 'increased' : 'decreased'} successfully`
      );
      form.resetFields();
      onSuccess?.();
    } catch (error) {
      message.error('Failed to adjust stock');
      console.error('Stock adjustment error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={`${adjustmentType === 'increase' ? 'Increase' : 'Decrease'} Stock - ${record?.medicine_name || 'Medicine'}`}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          currentStock: record?.current_stock || 0,
          quantity: 1,
          reason: '',
        }}
      >
        <Form.Item
          label="Current Stock"
          name="currentStock"
        >
          <InputNumber
            disabled
            style={{ width: '100%' }}
            formatter={(value) => `${value} units`}
          />
        </Form.Item>

        <Form.Item
          label={`${adjustmentType === 'increase' ? 'Quantity to Add' : 'Quantity to Remove'}`}
          name="quantity"
          rules={[
            { required: true, message: 'Please enter quantity' },
            { 
              type: 'number', 
              min: 1, 
              message: 'Quantity must be at least 1' 
            },
            {
              validator: (_, value) => {
                if (adjustmentType === 'decrease' && value > (record?.current_stock || 0)) {
                  return Promise.reject('Cannot remove more than current stock');
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            min={1}
            max={adjustmentType === 'decrease' ? record?.current_stock || 0 : undefined}
            style={{ width: '100%' }}
            placeholder="Enter quantity"
          />
        </Form.Item>

        <Form.Item
          label="Reason for Adjustment"
          name="reason"
          rules={[
            { required: true, message: 'Please provide a reason' },
            { min: 3, message: 'Reason must be at least 3 characters' },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Enter reason for stock adjustment..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isLoading}
              danger={adjustmentType === 'decrease'}
            >
              {adjustmentType === 'increase' ? 'Increase Stock' : 'Decrease Stock'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};
